import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  SlidersHorizontal as Filters,
  X,
  Calendar,
  Briefcase,
  User,
  ClipboardList,
  IndianRupee,
  Loader2
} from 'lucide-react';
import Pagination from '../../components/Common/Pagination/Pagination';
import { useLoading } from '../../context/LoadingContext';
import styles from './ManufacturerClearance.module.css';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const ManufacturerClearance = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [clearances, setClearances] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    project_id: '',
    vendor_id: ''
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    vendors: []
  });

  const [isTableLoading, setIsTableLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    fetchFilterData();
    fetchClearances();
    return () => window.removeEventListener('resize', handleResize);
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  const fetchFilterData = async () => {
    try {
      const [projectsRes, vendorsRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown')
      ]);
      setFilterData({
        projects: projectsRes.data.data || [],
        vendors: vendorsRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchClearances = async () => {
    setIsTableLoading(true);
    try {
      const response = await api.get('/manufacturer-clearances', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        },
        skipLoader: true
      });

      if (response.data.status === 'success') {
        // Handle both paginated and non-paginated responses
        const data = response.data.data;
        if (data.data) {
          setClearances(data.data);
          setTotalItems(data.total);
        } else {
          setClearances(data);
          setTotalItems(data.length);
        }
      }
    } catch (error) {
      console.error('Error fetching clearances:', error);
      toast.error('Failed to fetch clearances');
    } finally {
      setIsTableLoading(false);
    }
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const toggleActionMenu = (index, e) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      setMenuPosition({ top: rect.bottom + 4, left: rect.right - 180 });
      setOpenMenuIndex(index);
    }
  };

  const handleFilterChange = (key, option) => {
    setFilters(prev => ({ ...prev, [key]: option ? option.value : '' }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      vendor_id: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleDelete = async (item) => {
    setOpenMenuIndex(null);
    
    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Record</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Clearance Record?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete this clearance record for PO <strong>${item.purchase_order?.po_number}</strong>?<br/>
          This action cannot be undone.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/manufacturer-clearances/${item.id}`);
          if (response.data.status === 'success') {
            toast.success('Clearance record deleted successfully');
            fetchClearances();
          }
        } catch (error) {
          console.error('Error deleting clearance:', error);
          toast.error('Failed to delete clearance record');
        }
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles['action-wrapper']}`) && !event.target.closest(`.${styles['action-dropdown']}`)) {
        setOpenMenuIndex(null);
      }
    };
    if (openMenuIndex !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuIndex]);

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  return (
    <div className={`${styles['clearance-list-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Manufacturer Clearance</h1>
              <p>View and manage all manufacturer clearance records.</p>
            </div>
            <button className={styles['add-btn']} onClick={() => navigate('/manufacturer-clearance/add')}>
              <Plus size={20} />
              <span>Record New Clearance</span>
            </button>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search clearances..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(option.value); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {activeFiltersCount > 0 && <span className={styles['filter-badge']} />}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Clearances</h3>
                  <p>Refine the list by project or vendor</p>
                </div>
                <button
                  className={styles['close-icon-btn']}
                  onClick={() => setShowFilters(false)}
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>By Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(p => p.id.toString() === filters.project_id)?.project_name || 'All Projects'
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt)}
                    options={filterData.projects.map(p => ({ value: p.id.toString(), label: p.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>By Vendor</label>
                  <CustomSelect
                    value={filters.vendor_id ? {
                      value: filters.vendor_id,
                      label: filterData.vendors.find(v => v.id.toString() === filters.vendor_id)?.vendor_name || 'All Vendors'
                    } : null}
                    onChange={(opt) => handleFilterChange('vendor_id', opt)}
                    options={filterData.vendors.map(v => ({ value: v.id.toString(), label: v.vendor_name }))}
                    placeholder="All Vendors"
                    isClearable={true}
                  />
                </div>
              </div>

              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>
                  Reset
                </button>
                <button className={styles['filters-close-btn']} onClick={() => setShowFilters(false)}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          <div className={styles['table-wrapper']}>
            <table className={styles['clearance-table']}>
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>PROJECT</th>
                  <th>VENDOR</th>
                  <th>PO NUMBER</th>
                  <th>AMOUNT</th>
                  <th>ADVANCE</th>
                  <th className={styles['action-cell']}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {isTableLoading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: '100px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <Loader2 size={40} className={styles['spinner']} style={{ color: '#f97316' }} />
                        <span style={{ color: '#64748b', fontWeight: 600 }}>Syncing clearance records...</span>
                      </div>
                    </td>
                  </tr>
                ) : clearances.length > 0 ? (
                  clearances.map((item, index) => (
                    <tr key={item.id}>
                      <td className={styles['date-cell']}>{new Date(item.date).toLocaleDateString('en-IN')}</td>
                      <td>{item.project?.project_name}</td>
                      <td>{item.vendor?.vendor_name}</td>
                      <td className={styles['po-cell']}>{item.purchase_order?.po_number}</td>
                      <td className={styles['amount-cell']}>₹ {parseFloat(item.amount).toLocaleString('en-IN')}</td>
                      <td>
                        <span className={`${styles['status-pill']} ${item.is_advance ? styles['pill-yes'] : styles['pill-no']}`}>
                          {item.is_advance ? 'YES' : 'NO'}
                        </span>
                      </td>
                      <td className={styles['action-cell']}>
                        <div className={styles['action-wrapper']}>
                          <button
                            className={`${styles['icon-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                            onClick={(e) => toggleActionMenu(index, e)}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  !loading && <NoData message="No clearance records found." colSpan={7} isTable={true} />
                )}
              </tbody>
            </table>
          </div>

          <div className={styles['clearance-cards']}>
            {isTableLoading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Loader2 size={32} className={styles['spinner']} style={{ color: '#f97316' }} />
              </div>
            ) : clearances.length > 0 ? (
              clearances.map((item, index) => (
                <div key={item.id} className={styles['clearance-card']}>
                  <div className={styles['card-main']}>
                    <div className={styles['card-info']}>
                      <span className={styles['card-date']}>
                        {new Date(item.date).toLocaleDateString('en-IN')}
                      </span>
                      <span className={`${styles['card-status-pill']} ${item.is_advance ? styles['pill-yes'] : styles['pill-no']}`}>
                        {item.is_advance ? 'ADVANCE: YES' : 'ADVANCE: NO'}
                      </span>
                    </div>
                    <div className={styles['action-wrapper']}>
                      <button
                        className={styles['icon-btn']}
                        onClick={(e) => toggleActionMenu(index, e)}
                      >
                        <MoreVertical size={18} />
                      </button>
                    </div>
                  </div>
                  <div className={styles['card-details']}>
                    <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                      <span className={styles['detail-label']}>Project</span>
                      <span className={styles['detail-value']}>{item.project?.project_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Vendor</span>
                      <span className={styles['detail-value']}>{item.vendor?.vendor_name}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>PO Number</span>
                      <span className={styles['detail-value']} style={{ color: '#f59e0b', fontWeight: 800 }}>{item.purchase_order?.po_number}</span>
                    </div>
                    <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                      <span className={styles['detail-label']}>Amount</span>
                      <span className={styles['detail-value']} style={{ fontSize: '15px', color: '#0f172a' }}>₹ {parseFloat(item.amount).toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              ))
              ) : (
                !loading && <NoData message="No clearance records found." isTable={false} />
              )}
          </div>

          <div className={styles['desktop-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>

          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button className={styles['dropdown-item']} onClick={() => navigate(`/manufacturer-clearance/view/${clearances[openMenuIndex]?.id}`)}>
                <Eye size={16} /> View Details
              </button>
              <button className={styles['dropdown-item']} onClick={() => navigate(`/manufacturer-clearance/edit/${clearances[openMenuIndex]?.id}`)}>
                <Edit size={16} /> Edit Record
              </button>
              <div className={styles['dropdown-divider']}></div>
              <button className={[styles['dropdown-item'], styles['delete-opt']].join(' ')} onClick={() => handleDelete(clearances[openMenuIndex])}>
                <Trash2 size={16} /> Delete
              </button>
            </div>
          )}

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default ManufacturerClearance;
